/**
 * IoT dashboard roles — internal levels stay 1–5 for API/auth compatibility.
 * User-facing labels collapse to three roles:
 *   Level 3 · Platform administrator  ← internal 5
 *   Level 2 · Organization administrator ← internal 4
 *   Level 1 · Building user ← internal 1–3
 */

const DISPLAY_ROLE = {
  3: {
    displayLevel: 3,
    title: "Platform administrator",
  },
  2: {
    displayLevel: 2,
    title: "Organization administrator",
  },
  1: {
    displayLevel: 1,
    title: "Building user",
  },
};

/**
 * Map stored dashboardRoleLevel (1–5) to the public 1–3 role tier.
 * @param {number|string|null|undefined} level
 * @returns {1|2|3|null}
 */
export function getDisplayRoleLevel(level) {
  const n = Number(level);
  if (!Number.isFinite(n) || n < 1) return null;
  if (n >= 5) return 3;
  if (n >= 4) return 2;
  return 1;
}

/**
 * @param {number|string|null|undefined} level - internal 1–5
 * @returns {{ displayLevel: number, title: string } | null}
 */
export function getIotDisplayRole(level) {
  const display = getDisplayRoleLevel(level);
  if (display == null) return null;
  return DISPLAY_ROLE[display];
}

/**
 * Short title for the role (no "Level N" prefix).
 * @param {number|string|null|undefined} level - internal 1–5
 */
export function iotDashboardRoleSummary(level) {
  const role = getIotDisplayRole(level);
  return role ? role.title : null;
}

/**
 * Badge / menu label, e.g. "Level 3 · Platform administrator".
 * @param {number|string|null|undefined} level - internal 1–5
 */
export function formatIotDashboardRoleLabel(level) {
  const role = getIotDisplayRole(level);
  if (!role) return null;
  return `Level ${role.displayLevel} · ${role.title}`;
}

/**
 * @param {object} user - row from GET /api/org-users
 * @returns {number | null} IoT level 1–5 if present
 */
export function getIotDashboardRoleLevel(user) {
  if (!user || typeof user !== "object") return null;
  const raw =
    user.iotDashboardRoleLevel ??
    user.serviceRoleLevel ??
    user.service_role_level;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/** Internal levels 1–2 are read-only; level 3+ may control sensors. */
export function canWriteToSensors(level) {
  return Number(level ?? 1) >= 3;
}

/** Internal level 4+ may set per-device in-building location labels. */
export function canEditDeviceLocation(level) {
  return Number(level ?? 1) >= 4;
}
